
import { ExternalLink } from "lucide-react";
import { FaInstagram } from "react-icons/fa";
import { Button } from "@/components/ui/button";
import { useInstagramFeed } from "@/hooks/useInstagramFeed";
import { usePageContent } from "@/hooks/usePageContent";
import { useSiteSettings } from "@/hooks/useSiteSettings";

export function InstagramFeed({ className = "bg-white" }: { className?: string }) {
  const { posts, loading, error, refresh } = useInstagramFeed();
  const { getText } = usePageContent('home');
  const { getSetting } = useSiteSettings();
  const profileUrl = getSetting('instagram_profile_url', 'https://www.instagram.com/brenda.nutricionista/');

  const sectionLabel = getText('instagram', 'label',    'Instagram');
  const title        = getText('instagram', 'title',    'Últimos Eventos');
  const subtitle     = getText('instagram', 'subtitle', 'Acompanhe meus eventos e workshops no Instagram');

  const handleInstagramClick = () => {
    if (/^https?:\/\//i.test(profileUrl)) window.open(profileUrl, '_blank', 'noopener,noreferrer');
  };

  // Estado de loading inicial
  if (loading && posts.length === 0) {
    return (
      <section className={`py-20 ${className}`}>
        <div className="container mx-auto px-6">
          <div className="text-center mb-14">
            <p className="section-label mb-3">{sectionLabel}</p>
            <h2 className="section-heading mb-3">{title}</h2>
            <p className="text-lg text-gray-500 max-w-xl mx-auto font-primary font-light">{subtitle}</p>
          </div>

          <div className="flex justify-center items-center min-h-[200px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-2 border-brand-primary border-t-transparent mx-auto mb-4"></div>
              <p className="text-gray-500 font-primary">Carregando posts...</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Estado de erro (mas ainda mostra posts em cache se existirem)
  if (error && posts.length === 0) {
    return (
      <section className={`py-20 ${className}`}>
        <div className="container mx-auto px-6">
          <div className="text-center mb-14">
            <p className="section-label mb-3">{sectionLabel}</p>
            <h2 className="section-heading mb-3">{title}</h2>
            <p className="text-lg text-gray-500 max-w-xl mx-auto font-primary font-light">{subtitle}</p>
          </div>

          <div className="text-center">
            <div className="bg-orange-50 border border-orange-100 rounded-2xl p-8 max-w-md mx-auto">
              <FaInstagram className="w-8 h-8 text-pink-500 mx-auto mb-3" />
              <p className="text-gray-700 font-bold font-primary mb-1">Não foi possível carregar os posts</p>
              <p className="text-sm text-gray-500 font-primary mb-4">{error}</p>
              <div className="flex flex-col gap-2">
                <button
                  onClick={refresh}
                  disabled={loading}
                  className="bg-brand-primary text-white px-6 py-2.5 rounded-full font-bold font-primary text-sm hover:bg-brand-primary/90 transition-colors disabled:opacity-50"
                >
                  {loading ? 'Carregando...' : 'Tentar novamente'}
                </button>
                <button
                  onClick={handleInstagramClick}
                  className="text-gray-500 font-primary text-sm hover:text-brand-primary transition-colors"
                >
                  Ver no Instagram →
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={`py-20 ${className}`}>
      <div className="container mx-auto px-6">
        <div className="text-center mb-12">
          <p className="section-label mb-3">{sectionLabel}</p>
          <div className="flex items-center justify-center gap-3 mb-3">
            <h2 className="section-heading">{title}</h2>
            {loading && <div className="animate-spin rounded-full h-5 w-5 border-2 border-brand-primary border-t-transparent" />}
          </div>
          <p className="text-lg text-gray-500 max-w-xl mx-auto font-primary font-light">
            {subtitle}
          </p>
        </div>
        
        {posts.length === 0 && (
          <div className="text-center py-8 text-gray-400 text-sm font-primary mb-6">
            Nenhum post disponível no momento.
          </div>
        )}

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6 max-w-4xl mx-auto">
          {posts.slice(0, 3).map((post) => (
            <div key={post.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden group">
              <div className="relative overflow-hidden">
                <img
                  src={post.image}
                  alt="Evento Instagram"
                  className="w-full h-60 object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = 'https://images.unsplash.com/photo-1498837167922-ddd27525d352?w=400&h=400&fit=crop';
                  }}
                />
                <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm rounded-full p-1.5 shadow-sm">
                  <FaInstagram className="w-4 h-4 text-pink-500" />
                </div>
              </div>
              <div className="p-4">
                <p className="text-gray-400 text-xs mb-2 font-light">{post.date}</p>
                <p className="text-gray-700 text-sm leading-relaxed line-clamp-3">{post.caption}</p>

                {post.permalink && (
                  <a
                    href={post.permalink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-brand-primary hover:text-brand-secondary font-semibold text-sm mt-3 transition-colors"
                  >
                    Ver no Instagram
                    <ExternalLink className="w-3 h-3 ml-1" />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
        
        <div className="text-center mt-8">
          <Button
            className="btn-cta rounded-full px-8 gap-2 hover:scale-105 transition-transform"
            onClick={handleInstagramClick}
          >
            <FaInstagram className="w-5 h-5" />
            Ver mais no Instagram
            <ExternalLink className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </section>
  );
}
